import { useEffect, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBulkJobs, useCancelBulkJob, useProposalThreshold, useStartBulkJob } from '../hooks/useKnowledge'
import type { BulkJob, ProposalAction } from '../api/knowledgeApi'

/**
 * Where accepting by confidence is safe, from spot-checks of the backlog run (Oct 2026): samples at
 * these levels were 19–25 of 25 right. Removals stop at 0.96 because the 0.95 level holds labels
 * better down-weighted than removed ("edges", "media"). Relations and splits are reviewed one by one.
 */
const RECOMMENDED: Partial<Record<ProposalAction, number>> = { category: 0.9, generic: 0.9, merge: 0.95, reject: 0.96 }
const NOTE: Partial<Record<ProposalAction, string>> = {
  link: 'Relations are the noisiest proposals — review them one by one.',
  split: 'Few enough to review one by one.',
}

const pct = (v: number) => v.toFixed(2)
const isOpen = (j: BulkJob) => j.status === 'queued' || j.status === 'running'

/** Time left at the job's rate so far. */
function eta(j: BulkJob): string | null {
  if (!j.accepted) return null
  const elapsed = (Date.now() - new Date(j.createdAt).getTime()) / 1000
  const secs = (elapsed / j.accepted) * Math.max(0, j.total - j.accepted)
  return secs < 90 ? `${Math.ceil(secs)} s` : `${Math.ceil(secs / 60)} min`
}

function JobProgress({ job, onCancel, cancelling }: { job: BulkJob; onCancel: () => void; cancelling: boolean }) {
  const share = Math.min(100, (100 * job.accepted) / Math.max(1, job.total))
  return (
    <div className="space-y-1">
      <div className="h-1.5 overflow-hidden rounded bg-slate-200">
        <div className="h-full bg-slate-900 transition-all" style={{ width: `${share}%` }} />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500 tabular-nums">
        <span>
          {job.status === 'queued' ? 'Starting…' : <>Accepted {job.accepted.toLocaleString()} of {job.total.toLocaleString()}</>}
          {eta(job) && <> · about {eta(job)} left</>}
          {' '}· at ≥ {pct(job.minConfidence)} · runs in the background
        </span>
        <button className="text-slate-600 underline hover:text-slate-900" disabled={cancelling} onClick={onCancel}>
          Stop
        </button>
      </div>
    </div>
  )
}

function LastJob({ job }: { job: BulkJob }) {
  const when = job.finishedAt ? formatDistanceToNow(new Date(job.finishedAt), { addSuffix: true }) : ''
  const tone = job.status === 'failed' || job.failed.length ? 'text-rose-700' : 'text-slate-500'
  return (
    <p className={`text-xs ${tone}`}>
      Last: {job.status === 'done' ? 'accepted' : job.status === 'cancelled' ? 'stopped after accepting' : 'failed after accepting'}{' '}
      {job.accepted.toLocaleString()} of {job.total.toLocaleString()} at ≥ {pct(job.minConfidence)} {when}
      {job.failed.length > 0 && <> · {job.failed.length} could not be applied ({job.failed[0].message})</>}
      {job.error && <> · {job.error}</>}
    </p>
  )
}

/** Accept every pending proposal of one action at or above a confidence, as a background job. */
export function BulkAcceptPanel({ action, label }: { action: ProposalAction; label: string }) {
  const recommended = RECOMMENDED[action]
  const [min, setMin] = useState<number>(recommended ?? 0.95)
  const [confirming, setConfirming] = useState(false)
  const { data: preview } = useProposalThreshold(action, min)
  const { data: jobs } = useBulkJobs()
  const start = useStartBulkJob()
  const cancel = useCancelBulkJob()

  useEffect(() => {
    setMin(RECOMMENDED[action] ?? 0.95)
    setConfirming(false)
  }, [action])
  useEffect(() => setConfirming(false), [min])

  const mine = (jobs?.data ?? []).filter((j) => j.action === action)
  const running = mine.find(isOpen)
  const last = mine.find((j) => !isOpen(j))
  const levels = (preview?.bands ?? []).filter((b) => b.band >= 0.5)

  return (
    <div className="space-y-3 rounded-md border bg-slate-50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Accept by confidence</span>
        <span className="text-slate-500">at or above</span>
        <select
          className="rounded border bg-white px-2 py-1 tabular-nums"
          value={min}
          disabled={Boolean(running)}
          onChange={(e) => setMin(Number(e.target.value))}
          aria-label="Minimum confidence"
        >
          {[...new Set([...(recommended ? [recommended] : []), ...levels.map((b) => b.band)])]
            .sort((a, b) => b - a)
            .map((v) => (
              <option key={v} value={v}>
                {pct(v)}{v === recommended ? ' (recommended)' : ''}
              </option>
            ))}
        </select>
        {preview && (
          <span className="text-slate-600">
            → <b className="tabular-nums">{preview.eligible.toLocaleString()}</b> {label.toLowerCase()}
            {' '}on {preview.artworkLinks.toLocaleString()} artwork links
            {preview.held > 0 && <>, {preview.held.toLocaleString()} held back (they contradict another proposal)</>}
          </span>
        )}
        {preview && preview.eligible > 0 && !running && (
          confirming ? (
            <>
              <Button
                size="sm"
                disabled={start.isPending}
                onClick={() => start.mutate({ action, minConfidence: min }, { onSettled: () => setConfirming(false) })}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" /> Confirm — accept {preview.eligible.toLocaleString()}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
              Accept {preview.eligible.toLocaleString()}
            </Button>
          )
        )}
      </div>

      {running && <JobProgress job={running} cancelling={cancel.isPending} onCancel={() => cancel.mutate(running.id)} />}
      {!running && last && <LastJob job={last} />}

      {levels.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          <span>Pending by the model&apos;s confidence:</span>
          {levels.map((b) => (
            <span key={b.band} className={`tabular-nums ${b.band >= min - 1e-4 ? 'text-slate-900' : ''}`}>
              {pct(b.band)} × {b.n.toLocaleString()}
            </span>
          ))}
        </div>
      )}
      {NOTE[action] && <p className="text-xs text-amber-700">{NOTE[action]}</p>}
      {action !== 'category' && action !== 'generic' && action !== 'link' && (
        <p className="text-xs text-slate-500">Accepted {label.toLowerCase()} apply on the next nightly build; each can be revoked under Rules.</p>
      )}
    </div>
  )
}
